import React from 'react';
import WeibullProbabilityPlot from './WeibullProbabilityPlot';
import WeibullCDFPlot from './WeibullCDFPlot';
import { WeibullAnalysisResult } from '@/utils/weibull';
import { CensoredDataPoint } from '@/utils/weibullCensored';
import { ColorByOption, MarkerByOption, SizeByOption } from '@/components/analysis/PlotCustomization';

interface DualPlotViewProps {
  analysis: WeibullAnalysisResult | null;
  isLoading: boolean;
  showConfidenceIntervals: boolean;
  confidenceLevel: number;
  showCensoredData: boolean;
  censoredData?: CensoredDataPoint[];
  showOnyxData: boolean;
  onyxCensorData: CensoredDataPoint[];
  selectedMethod?: 'johnson' | 'mle';
  colorBy?: ColorByOption;
  markerBy?: MarkerByOption;
  sizeBy?: SizeByOption;
  showLegend?: boolean;
  hasCalculatedAnalysis?: boolean;
  groupTiedTimes?: boolean;
}

/**
 * Side-by-side view of both Weibull probability and CDF plots
 * Only shown when viewport is wide enough and no sidebars are open
 */
const DualPlotView: React.FC<DualPlotViewProps> = ({
  analysis,
  isLoading,
  showConfidenceIntervals,
  confidenceLevel,
  showCensoredData,
  censoredData,
  showOnyxData,
  onyxCensorData,
  selectedMethod: _selectedMethod = 'mle',
  colorBy = 'none',
  markerBy = 'none',
  sizeBy = 'uniform',
  showLegend = false,
  hasCalculatedAnalysis = false,
  groupTiedTimes = true,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6 h-[500px] flex items-center justify-center">
          <div className="text-gray-500">Loading probability plot...</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 h-[500px] flex items-center justify-center">
          <div className="text-gray-500">Loading CDF plot...</div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="grid grid-cols-1 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6 h-[500px] flex items-center justify-center">
          <div className="text-gray-500">
            {hasCalculatedAnalysis
              ? 'No Analysis available. Please check applied filters and selected records'
              : "Select filter values in the sidebar and click 'Calculate Analysis' to begin"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 mb-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Probability Plot */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-700">
              Weibull Probability Plot
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Linearized view for assessing fit quality
            </p>
          </div>
          <div className="pt-2 px-6 pb-4" style={{ height: '480px' }}>
            <WeibullProbabilityPlot
              analysis={analysis}
              showConfidenceIntervals={showConfidenceIntervals}
              confidenceLevel={confidenceLevel}
              showCensoredData={showCensoredData}
              censoredData={censoredData}
              showOnyxData={showOnyxData}
              onyxCensorData={onyxCensorData}
              height={440}
              colorBy={colorBy}
              markerBy={markerBy}
              sizeBy={sizeBy}
              showLegend={showLegend}
              groupTiedTimes={groupTiedTimes}
            />
          </div>
        </div>

        {/* CDF Plot */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-700">
              Cumulative Distribution Function
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Probability of failure over time
            </p>
          </div>
          <div className="pt-2 px-6 pb-4" style={{ height: '480px' }}>
            <WeibullCDFPlot
              analysis={analysis}
              showConfidenceIntervals={showConfidenceIntervals}
              confidenceLevel={confidenceLevel}
              showCensoredData={showCensoredData}
              censoredData={censoredData}
              showOnyxData={showOnyxData}
              onyxCensorData={onyxCensorData}
              height={440}
              colorBy={colorBy}
              markerBy={markerBy}
              sizeBy={sizeBy}
              showLegend={showLegend}
              groupTiedTimes={groupTiedTimes}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DualPlotView;
